import { useState } from 'react';
import { useTick } from '@pixi/react';
import { projectileService } from '../../services/ProjectileService';
import EntityDisplay from './EntityElements/EntityDisplay';
import { tileWidth, tileHeight } from '../../data/tileset.config';
import { getZIndex } from '../../utils/zindex.utils';
import { DirectionEnum } from '../../types/enums';
import {
  ensureGridSheet,
  getGridSheetDescriptor,
} from '../../services/LazySpritesheetService';
import { useSpritesStore } from '../../store/spritesSlice';

// Projectile bullet sheets are authored at 24px/cell, too small on the grid —
// scale the rendered sprite up (purely visual, collision stays cell-based).
const PROJECTILE_VISUAL_SCALE = 1;

// Bullet art is single-direction (faces left, animated leftward). EntityDisplay
// mirrors it horizontally for the right-facing directions (TOP-RIGHT / RIGHT /
// BOTTOM-RIGHT), so LEFT and RIGHT need no rotation. The remaining six get a
// turn so the bullet points along its travel: ±90° for the verticals and ±45°
// for the diagonals. Angles account for the flip (a mirrored sprite turns the
// opposite way), verified against the cardinal cases.
function bulletRotation(direction: DirectionEnum): number {
  switch (direction) {
    case DirectionEnum.TOP:
      return Math.PI / 2;
    case DirectionEnum.BOTTOM:
      return -Math.PI / 2;
    case DirectionEnum['TOP-RIGHT']:
      return -Math.PI / 4;
    case DirectionEnum['BOTTOM-RIGHT']:
      return Math.PI / 4;
    case DirectionEnum['TOP-LEFT']:
      return Math.PI / 4;
    case DirectionEnum['BOTTOM-LEFT']:
      return -Math.PI / 4;
    default:
      return 0;
  }
}

export const ProjectileRenderer = () => {
  const [, setRenderTick] = useState(0);
  const textures = useSpritesStore((s) => s.textures);
  // Projectiles move continuously, so re-render every frame — unlike
  // SpellAnimationsRenderer, which only re-renders when the set of ids changes.
  useTick(() => setRenderTick((t) => (t + 1) % 1_000_000));

  return (
    <>
      {projectileService.getRenderProjectiles().map((p) => {
        ensureGridSheet(p.sprite);
        // Skip a projectile whose sheet hasn't landed yet rather than flash a
        // placeholder for a frame; it appears once the texture is in the store.
        if (!textures[p.sprite]) {
          return null;
        }
        const posY = p.cellY + p.progress * p.dy;
        const grid = getGridSheetDescriptor(p.sprite);
        return (
          <EntityDisplay
            key={p.id}
            clip={{
              spriteName: p.sprite,
              direction: p.direction,
              rotation: grid ? bulletRotation(p.direction) : 0,
              loop: true,
              width: grid ? grid.cell * PROJECTILE_VISUAL_SCALE : undefined,
              height: grid ? grid.cell * PROJECTILE_VISUAL_SCALE : undefined,
            }}
            position={{
              x: (p.cellX + p.progress * p.dx) * tileWidth,
              y: posY * tileHeight,
            }}
            name={p.id}
            // Y-sort like entities so a projectile passes behind / in front by depth.
            zIndex={getZIndex({ type: 'entity', posY })}
          />
        );
      })}
    </>
  );
};
